import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { MetricsChart } from 'components/MetricsChart';
import { ChartData } from 'components/chart/data/ChartData';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { BarChartRenderer } from 'components/chart/renderer/BarChartRenderer';
import { TileContent } from 'components/Layout/PageContainer';
import { Measure, useModelService } from 'services/model/model.service';
import { tbPalette } from 'styles/colors';
import { EmptyState } from 'components/EmptyState';

interface FulfillmentMethodSharesChartProps<T> {
  data: T[];
  name: (item: T) => string;
  fulfillmentMethods: (item: T) => {
    attributes?: { chnFulfillmentMethod?: string };
    values?: Record<string, number>;
  }[];
  gmvMeasure: Measure;
  topN?: number;
}

export function FulfillmentMethodSharesChart<T>({
  data,
  name,
  fulfillmentMethods,
  gmvMeasure,
  topN = 10,
}: FulfillmentMethodSharesChartProps<T>) {
  const intl = useIntl();

  const { getAttribute } = useModelService();

  const chnFulfillmentMethod = getAttribute('chnFulfillmentMethod');

  const COLORS = {
    brand: tbPalette.data.vis.default,
    marketplace: tbPalette.data.vis.critical,
  };

  const getTotalGmv = (item: T) =>
    fulfillmentMethods(item)?.reduce((sum, fulfillment) => sum + gmvMeasure.getValue(fulfillment.values), 0) || 0;

  const getBreakdownValue = (item: T, method: string) => {
    const match = fulfillmentMethods(item)?.find(
      fulfillment => chnFulfillmentMethod.getValue(fulfillment.attributes) === method
    );
    return match ? gmvMeasure.getValue(match.values) : 0;
  };

  const { topItems, otherItems } = useMemo(() => {
    const sumGmv = (item: T) =>
      fulfillmentMethods(item)?.reduce((sum, f) => sum + gmvMeasure.getValue(f.values), 0) || 0;

    const sorted = [...data].sort((a, b) => sumGmv(b) - sumGmv(a));
    return {
      topItems: sorted.slice(0, topN).reverse(),
      otherItems: sorted.slice(topN),
    };
  }, [data, fulfillmentMethods, topN, gmvMeasure]);

  const chartData = useMemo(() => {
    const othersLabel = intl.formatMessage({
      id: 'groupby.others',
      defaultMessage: 'Others',
    });

    const buildDataArray = (type: ChartDataType, label: string, accessor: (item: T) => number, color?: string) => {
      const topData = topItems.map(item => new ChartData(name(item), accessor(item)));

      const values =
        otherItems.length > 0
          ? [
              new ChartData(
                othersLabel,
                otherItems.reduce((sum, item) => sum + accessor(item), 0)
              ),
              ...topData,
            ]
          : topData;

      const chartArray = new ChartDataArray(type, label, values);
      if (color) chartArray.color = color;
      return chartArray;
    };

    const primary = buildDataArray(ChartDataType.primary, 'all', getTotalGmv);

    const brandData = buildDataArray(
      ChartDataType.breakdown,
      intl.formatMessage({
        id: 'fulfillment.method.brand',
        defaultMessage: 'Fulfilled by Brand',
      }),
      item => getBreakdownValue(item, 'brand'),
      COLORS.brand
    );

    const marketplaceData = buildDataArray(
      ChartDataType.breakdown,
      intl.formatMessage({
        id: 'fulfillment.method.marketplace',
        defaultMessage: 'Fulfilled by Marketplace',
      }),
      item => getBreakdownValue(item, 'marketplace'),
      COLORS.marketplace
    );

    return [primary, brandData, marketplaceData];
  }, [intl, name, topItems, otherItems, COLORS]);

  const config = useMemo(() => {
    const cfg = new BarChartConfig();
    cfg.value = {
      code: gmvMeasure.code,
      label: gmvMeasure.metric.name,
      unit: gmvMeasure.metric.unit,
    };
    cfg.breakdownMode = 'fill-stacked';
    cfg.legendConfig = { hide: false };
    cfg.data = chartData;
    return cfg;
  }, [chartData, gmvMeasure]);

  const renderer = new BarChartRenderer(config);

  if (topItems.length === 0) {
    return <EmptyState />;
  }
  return (
    <TileContent>
      <MetricsChart renderer={renderer} height="h-[400px]" />
    </TileContent>
  );
}
